/**
 * SanctaMarkdown — lightweight inline markdown renderer for Sancta replies.
 *
 * Handles the four constructs the model actually uses today:
 *   **bold**         → <strong>bold</strong>
 *   *italic*         → <em>italic</em>
 *   `code`           → <code>code</code>
 *   [text](https://) → <a href> new-tab link
 *
 * Everything else falls through as plain text with line breaks preserved
 * (parent bubble has `whitespace-pre-wrap`).
 *
 * Deliberately DOES NOT pull in a full markdown library — the message
 * bodies are short and any heavier renderer would double the widget's
 * JS budget. If future features need lists/tables we'll swap this out.
 */
import React from "react";

// Order matters: bold must be tested BEFORE italic because ** contains *.
type Token = { type: "text" | "bold" | "italic" | "code" | "link"; content: string; href?: string };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  const re = /(\*\*([^*\n]+?)\*\*)|(\*([^*\n]+?)\*)|(`([^`\n]+?)`)|(\[([^\]]+?)\]\((https?:\/\/[^\s)]+)\))/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ type: "text", content: input.slice(lastIndex, m.index) });
    }
    if (m[1]) {
      tokens.push({ type: "bold", content: m[2] });
    } else if (m[3]) {
      tokens.push({ type: "italic", content: m[4] });
    } else if (m[5]) {
      tokens.push({ type: "code", content: m[6] });
    } else if (m[7]) {
      tokens.push({ type: "link", content: m[8], href: m[9] });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < input.length) {
    tokens.push({ type: "text", content: input.slice(lastIndex) });
  }
  return tokens;
}

export const SanctaMarkdown: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const tokens = React.useMemo(() => tokenize(text || ""), [text]);
  if (!text) return null;
  return (
    <span className={className}>
      {tokens.map((t, i) => {
        if (t.type === "bold")   return <strong key={i} className="font-semibold">{t.content}</strong>;
        if (t.type === "italic") return <em key={i} className="italic">{t.content}</em>;
        if (t.type === "code")   return <code key={i} className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 font-mono text-[0.9em]">{t.content}</code>;
        if (t.type === "link")   return (
          <a
            key={i}
            href={t.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-fuchsia-600 dark:text-fuchsia-300 underline underline-offset-2 hover:text-fuchsia-700"
          >
            {t.content}
          </a>
        );
        return <React.Fragment key={i}>{t.content}</React.Fragment>;
      })}
    </span>
  );
};

export default SanctaMarkdown;
